import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_URL, API_URL } from '../env';
import { formatCoins, tokensToCoins } from '../coins';

type CatalogOffer = {
  packId?: string;
  planId?: string;
  amountCents: number;
  monthlyEquivalentCents?: number | null;
  pricePerCoinCents?: number | null;
  paddleReady: boolean;
};

type CatalogOption = {
  ticks: number;
  coins: number;
  docsIncluded: number;
  accountsSharing?: string;
  oneTime: CatalogOffer;
  yearly: CatalogOffer | null;
};

type CatalogTier = {
  plan: string;
  contactOnly: boolean;
  accountsSharing?: string;
  popular?: boolean;
  options: CatalogOption[];
};

/** Hardcoded seat ranges by plan (locked display). */
const ACCOUNT_RANGES: Record<string, string> = {
  Trial: '1',
  Startup: '1–4',
  Growth: '5–29',
  Pro: '30–99',
  Enterprise: '100+',
};

const TRIAL_COINS_FALLBACK = 150;

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatPerCoin(cents: number | null | undefined) {
  if (cents == null || !Number.isFinite(cents)) return null;
  return `$${(cents / 100).toFixed(3)}/coin`;
}

function plusAccounts(label?: string) {
  const s = String(label || '').trim();
  if (/^\d+\+$/.test(s)) return `+${s.slice(0, -1)}`;
  if (/^\d+$/.test(s)) return `${s}+`;
  return s || '—';
}

function PackCarousel({
  options,
  selectedTicks,
  onSelect,
  subscription,
}: {
  options: CatalogOption[];
  selectedTicks: number;
  onSelect: (ticks: number) => void;
  subscription: boolean;
}) {
  const idx = Math.max(
    0,
    options.findIndex((o) => o.ticks === selectedTicks),
  );
  const opt = options[idx] || options[0];
  if (!opt) return null;
  const rate = formatPerCoin(
    subscription ? opt.yearly?.pricePerCoinCents : opt.oneTime?.pricePerCoinCents,
  );
  const canPrev = idx > 0;
  const canNext = idx < options.length - 1;

  return (
    <div style={{ margin: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          aria-label="Previous pack"
          disabled={!canPrev}
          onClick={() => canPrev && onSelect(options[idx - 1].ticks)}
          style={carouselNavStyle(canPrev)}
        >
          ‹
        </button>
        <div
          style={{
            flex: 1,
            textAlign: 'center',
            padding: '10px 8px',
            borderRadius: 10,
            border: '1px solid rgba(37,99,235,0.45)',
            background: 'rgba(37,99,235,0.15)',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#f1f5f9' }}>
            {formatCoins(opt.coins)} {subscription ? 'Coins/Mo' : 'Coins'}
          </div>
          {rate ? (
            <span style={{ display: 'block', marginTop: 4, color: '#4ade80', fontSize: '0.7rem', fontWeight: 600 }}>
              {rate}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Next pack"
          disabled={!canNext}
          onClick={() => canNext && onSelect(options[idx + 1].ticks)}
          style={carouselNavStyle(canNext)}
        >
          ›
        </button>
      </div>
    </div>
  );
}

function carouselNavStyle(enabled: boolean): React.CSSProperties {
  return {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 8,
    border: '1px solid rgba(148,163,184,0.25)',
    background: '#0f172a',
    color: '#e2e8f0',
    fontSize: '1rem',
    lineHeight: 1,
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled ? 1 : 0.35,
  };
}

const PricingPage: React.FC = () => {
  const [tiers, setTiers] = useState<CatalogTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(true);
  const [selectedTicks, setSelectedTicks] = useState<Record<string, number>>({});
  const [trialDocs, setTrialDocs] = useState(40);
  const [trialCoins, setTrialCoins] = useState(TRIAL_COINS_FALLBACK);

  useEffect(() => {
    fetch(`${API_URL}/billing/volume-catalog`)
      .then((r) => r.json())
      .then((data) => {
        const list: CatalogTier[] = data?.tiers || [];
        setTiers(list);
        if (data?.subscriptionDefault === false) setSubscription(false);
        const initial: Record<string, number> = {};
        for (const t of list) {
          if (t.options?.length) initial[t.plan] = t.options[0].ticks;
        }
        setSelectedTicks(initial);
      })
      .catch(() => setTiers([]))
      .finally(() => setLoading(false));

    fetch(`${API_URL}/config/trial`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.trialDocs) setTrialDocs(data.trialDocs);
        const tokens = Number(data?.trialTokens ?? data?.trialDocs);
        if (Number.isFinite(tokens) && tokens > 0) {
          const coins = tokensToCoins(tokens);
          setTrialCoins(coins > 0 ? coins : TRIAL_COINS_FALLBACK);
        } else if (Number.isFinite(Number(data?.coins)) && Number(data.coins) > 0) {
          setTrialCoins(Number(data.coins));
        }
      })
      .catch(() => {});
  }, []);

  const paidTiers = useMemo(() => tiers.filter((t) => !t.contactOnly), [tiers]);

  const paidOpt = (tier: CatalogTier) => {
    const ticks = selectedTicks[tier.plan] ?? tier.options[0]?.ticks;
    return tier.options.find((o) => o.ticks === ticks) || tier.options[0];
  };

  const renderPaidPrice = (tier: CatalogTier) => {
    const opt = paidOpt(tier);
    const offer = subscription ? opt?.yearly : opt?.oneTime;
    const priceCents = offer?.amountCents;
    const monthlyEq =
      subscription && offer && 'monthlyEquivalentCents' in offer
        ? (offer as CatalogOffer).monthlyEquivalentCents
        : null;

    if (!opt || priceCents == null) {
      return <span style={{ color: '#64748b' }}>No packs yet</span>;
    }

    if (subscription) {
      return (
        <>
          <div>
            <span style={{ fontSize: '1.35rem', fontWeight: 900 }}>
              {monthlyEq != null ? formatMoney(monthlyEq) : formatMoney(Math.round(priceCents / 12))}
            </span>
            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>/mo</span>
          </div>
          <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: 4 }}>
            {formatMoney(priceCents)}/yr
          </div>
        </>
      );
    }

    return (
      <div>
        <span style={{ fontSize: '1.35rem', fontWeight: 900 }}>{formatMoney(priceCents)}</span>
        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}> One Month</span>
      </div>
    );
  };

  const cell: React.CSSProperties = {
    verticalAlign: 'top',
    padding: '14px 12px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(148,163,184,0.12)',
    color: '#e2e8f0',
    fontSize: '0.9rem',
  };

  const headCell: React.CSSProperties = {
    ...cell,
    position: 'relative',
    paddingTop: 22,
    background: 'rgba(15,23,42,0.55)',
    borderBottom: '1px solid rgba(148,163,184,0.2)',
  };

  const rowLabel: React.CSSProperties = {
    ...cell,
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    width: 88,
    background: 'transparent',
  };

  return (
    <main style={{ paddingTop: 100 }}>
      <section style={{ padding: '60px 0 40px', textAlign: 'center' }}>
        <div className="container">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 16px',
              background: 'rgba(37,99,235,0.15)',
              border: '1px solid rgba(37,99,235,0.3)',
              borderRadius: 100,
              fontSize: '0.8rem',
              color: '#93c5fd',
              fontWeight: 500,
              marginBottom: 24,
            }}
          >
            Volume plans for every team size
          </div>
          <h1
            style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 900,
              marginBottom: 16,
              letterSpacing: '-0.02em',
            }}
          >
            Choose a plan. Pick your coin pack.
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: 560, margin: '0 auto 12px' }}>
            Larger packs unlock a better coin rate. Annual subscription is recommended for the best price.
          </p>
        </div>
      </section>

      <section style={{ padding: '0 0 24px', textAlign: 'center' }}>
        <div className="container">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 16px',
              background: '#1e293b',
              border: '1px solid rgba(148,163,184,0.2)',
              borderRadius: 12,
            }}
          >
            <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Billing</span>
            <button
              type="button"
              onClick={() => setSubscription(false)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.85rem',
                background: !subscription ? '#2563eb' : 'transparent',
                color: !subscription ? '#fff' : '#94a3b8',
              }}
            >
              One month
            </button>
            <button
              type="button"
              onClick={() => setSubscription(true)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.85rem',
                background: subscription ? '#2563eb' : 'transparent',
                color: subscription ? '#fff' : '#94a3b8',
              }}
            >
              Subscription 1y
              <span
                style={{
                  marginLeft: 8,
                  fontSize: '0.7rem',
                  background: 'rgba(34,197,94,0.2)',
                  color: '#4ade80',
                  padding: '2px 8px',
                  borderRadius: 100,
                }}
              >
                Recommended
              </span>
            </button>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 12 }}>
            {subscription
              ? 'Pay annually. Coins renew monthly. Better rate than one-month.'
              : 'One-month pack · coins added to your balance'}
          </p>
        </div>
      </section>

      <section style={{ padding: '20px 0 80px' }}>
        <div className="container">
          {loading ? (
            <div
              style={{
                background: '#1e293b',
                borderRadius: 20,
                height: 420,
                border: '1px solid rgba(148,163,184,0.15)',
              }}
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                className="upgrade-compare-table"
                style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  minWidth: 720,
                  background: '#1e293b',
                  borderRadius: 16,
                  overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.15)',
                }}
              >
                <thead>
                  <tr>
                    <th style={rowLabel} scope="col" />
                    <th style={headCell} scope="col">
                      <span style={{ display: 'block', fontWeight: 800, fontSize: '1.05rem' }}>Trial</span>
                    </th>
                    {paidTiers.map((tier) => (
                      <th
                        key={tier.plan}
                        scope="col"
                        style={{
                          ...headCell,
                          ...(tier.popular
                            ? {
                                background: 'rgba(37,99,235,0.12)',
                                boxShadow: 'inset 0 0 0 1px rgba(37,99,235,0.45)',
                              }
                            : {}),
                        }}
                      >
                        {tier.popular && (
                          <div
                            style={{
                              position: 'absolute',
                              top: -10,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: 'var(--primary-gradient)',
                              color: 'white',
                              padding: '3px 12px',
                              borderRadius: 100,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            MOST POPULAR
                          </div>
                        )}
                        <span style={{ display: 'block', fontWeight: 800, fontSize: '1.05rem' }}>
                          {tier.plan}
                        </span>
                      </th>
                    ))}
                    <th
                      scope="col"
                      style={{
                        ...headCell,
                        background: 'rgba(52,211,153,0.08)',
                      }}
                    >
                      <span style={{ display: 'block', fontWeight: 800, fontSize: '1.05rem', color: '#34d399' }}>
                        Enterprise
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row" style={rowLabel}>
                      Price
                    </th>
                    <td style={cell}>
                      <span style={{ fontSize: '1.35rem', fontWeight: 900 }}>Free</span>
                    </td>
                    {paidTiers.map((tier) => (
                      <td key={`price-${tier.plan}`} style={cell}>
                        {renderPaidPrice(tier)}
                      </td>
                    ))}
                    <td style={cell}>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>Contact sales</span>
                      <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 4 }}>—</div>
                    </td>
                  </tr>

                  <tr>
                    <th scope="row" style={rowLabel}>
                      Coins
                    </th>
                    <td style={cell}>
                      <span style={{ fontWeight: 700 }}>{formatCoins(trialCoins)} coins</span>
                    </td>
                    {paidTiers.map((tier) => {
                      const opt = paidOpt(tier);
                      return (
                        <td key={`coins-${tier.plan}`} style={cell}>
                          {opt ? (
                            <PackCarousel
                              options={tier.options}
                              selectedTicks={opt.ticks}
                              onSelect={(tk) => setSelectedTicks((s) => ({ ...s, [tier.plan]: tk }))}
                              subscription={subscription}
                            />
                          ) : (
                            <span style={{ color: '#64748b' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                    <td style={cell}>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>Custom</span>
                    </td>
                  </tr>

                  <tr>
                    <th scope="row" style={rowLabel}>
                      Accounts
                    </th>
                    <td style={cell}>{ACCOUNT_RANGES.Trial}</td>
                    {paidTiers.map((tier) => (
                      <td key={`accounts-${tier.plan}`} style={cell}>
                        {ACCOUNT_RANGES[tier.plan] || plusAccounts(tier.accountsSharing)}
                      </td>
                    ))}
                    <td style={cell}>{ACCOUNT_RANGES.Enterprise}</td>
                  </tr>

                  <tr>
                    <th scope="row" style={rowLabel}>
                      Term
                    </th>
                    <td style={cell}>1 month</td>
                    {paidTiers.map((tier) => (
                      <td key={`term-${tier.plan}`} style={cell}>
                        {subscription ? 'Subscription 1y' : 'One month'}
                      </td>
                    ))}
                    <td style={cell}>Custom</td>
                  </tr>

                  <tr>
                    <th scope="row" style={{ ...rowLabel, borderBottom: 'none' }}>
                      {' '}
                    </th>
                    <td style={{ ...cell, borderBottom: 'none', paddingTop: 18 }}>
                      <a href={APP_URL} style={ctaStyle(false)}>
                        Get started →
                      </a>
                    </td>
                    {paidTiers.map((tier) => {
                      const opt = paidOpt(tier);
                      const offer = subscription ? opt?.yearly : opt?.oneTime;
                      const ready = !!offer?.paddleReady;
                      return (
                        <td key={`cta-${tier.plan}`} style={{ ...cell, borderBottom: 'none', paddingTop: 18 }}>
                          <a
                            href={ready ? APP_URL : undefined}
                            style={{
                              ...ctaStyle(!!tier.popular && ready),
                              opacity: ready ? 1 : 0.45,
                              pointerEvents: ready ? 'auto' : 'none',
                            }}
                          >
                            {ready ? 'Get started →' : 'Coming soon'}
                          </a>
                        </td>
                      );
                    })}
                    <td style={{ ...cell, borderBottom: 'none', paddingTop: 18 }}>
                      <Link to="/contact/sales" style={ctaStyle(true)}>
                        Contact sales →
                      </Link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section style={{ padding: '40px 0 80px' }}>
        <div className="container" style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: 40 }}>Frequently Asked Questions</h2>
          {[
            {
              q: 'Why is subscription recommended?',
              a: 'Annual billing unlocks a better coin rate. Coins still renew monthly so your team stays funded all year.',
            },
            {
              q: 'Do larger packs cost less per coin?',
              a: 'Yes. Quantity discounts step down the markup as you pick more coins within a plan.',
            },
            {
              q: 'What about Enterprise?',
              a: 'Enterprise is custom — use Contact sales. We do not publish commercial numbers for that tier on the website.',
            },
            {
              q: 'Do you offer a free trial?',
              a: `Yes! Sign up free and get ${trialDocs} documents to process at no cost. No credit card required.`,
            },
          ].map((faq, i) => (
            <div
              key={i}
              style={{
                background: '#1e293b',
                border: '1px solid rgba(148,163,184,0.15)',
                borderRadius: 12,
                padding: 24,
                marginBottom: 16,
                textAlign: 'left',
              }}
            >
              <h4 style={{ fontWeight: 700, marginBottom: 10, fontSize: '1rem' }}>{faq.q}</h4>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

function ctaStyle(primary: boolean): React.CSSProperties {
  return {
    display: 'inline-block',
    textAlign: 'center',
    padding: '12px 18px',
    background: primary ? 'var(--primary-gradient)' : 'transparent',
    border: primary ? 'none' : '1px solid rgba(148,163,184,0.3)',
    color: 'white',
    borderRadius: 9,
    fontWeight: 600,
    fontSize: '0.9rem',
    textDecoration: 'none',
    width: '100%',
    maxWidth: 180,
    boxSizing: 'border-box',
  };
}

export default PricingPage;
